/**
 * printerProfilesStore.ts
 * Saved "printer profiles" — named paper setups so an office with several
 * printers (A4 laser, A5, thermal 80mm) can apply the right geometry in one
 * click instead of re-picking paper size + orientation every time.
 *
 * Persisted in localStorage; purely client-side (no PII).
 */

import type { CustomPaperMm, Orientation, PaperSize } from "@/types/print";

const STORAGE_KEY = "tvs-excel-uploader:printer-profiles";
const MAX_PROFILES = 20;

export interface PrinterProfile {
  id: string;
  /** Display name, e.g. "Máy in A5 kho" or "Máy nhiệt 80mm". */
  name: string;
  paperSize: PaperSize;
  orientation: Orientation;
  customMm?: CustomPaperMm;
  /** Free-text note (location, driver, tray…). */
  note?: string;
  /** When the profile was last used/edited (epoch ms). */
  updatedAt: number;
}

/** Read-only accessor (safe if localStorage is unavailable / corrupted). */
export function listPrinterProfiles(): PrinterProfile[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPrinterProfile).slice(0, MAX_PROFILES);
  } catch {
    return [];
  }
}

/** Unique-ish id for a new profile (timestamp + random suffix). */
function newProfileId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `pp-${ts}-${rand}`;
}

/** Save (upsert by id) one profile. Returns the stored profile. */
export function savePrinterProfile(
  profile: Omit<PrinterProfile, "id" | "updatedAt"> & Partial<Pick<PrinterProfile, "id" | "updatedAt">>
): PrinterProfile {
  const full: PrinterProfile = {
    id: profile.id ?? newProfileId(),
    updatedAt: profile.updatedAt ?? Date.now(),
    ...profile,
  } as PrinterProfile;
  if (typeof localStorage === "undefined") return full;
  try {
    const next = [full, ...listPrinterProfiles().filter((p) => p.id !== full.id)].slice(0, MAX_PROFILES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return full;
}

/** Delete a profile by id. */
export function deletePrinterProfile(id: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    const next = listPrinterProfiles().filter((p) => p.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/** Touch updatedAt on the profile with this id (e.g. when it was applied). */
export function touchPrinterProfile(id: string): void {
  const profile = listPrinterProfiles().find((p) => p.id === id);
  if (!profile) return;
  savePrinterProfile({ ...profile, updatedAt: Date.now() });
}

function isPrinterProfile(v: unknown): v is PrinterProfile {
  if (!v || typeof v !== "object") return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.id === "string" &&
    typeof p.name === "string" &&
    typeof p.paperSize === "string" &&
    typeof p.orientation === "string"
  );
}
